import gql from 'graphql-tag';
import { getRequirementQueryWithLevels } from 'Queries/GraphQlQueries/Requirement/RequirementQueries';
import { store } from 'Models/Store';
import { IProgramVersionEntityAttributes, ProgramVersionEntity } from 'Models/Entities';
import { getProgramRulesRecursiveQuery } from 'Queries/GraphQlQueries/ProgramRule/ProgramRuleQueries';
import { AbortSignals } from 'Hooks/useAbortSignals';
import { Conditions } from 'Util/QueryHelpers';

export const GET_PROGRAM_VERSION_NAMES_WITH_CAMPUS = gql`
	query getProgramVersionNames(
		$conditions: [[WhereExpressionGraph]]
	) {
		programVersionEntitys(
			conditions: $conditions
		) {
			id
			versionDisplayName
			commencingYear
			status
			program {
				id
				displayName
			}
			campuss {
				id
				campus {
					id
					name
				}
			}
		}
	}
`;

export const COUNT_PROGRAM_VERSIONS_WITH_VERSION_LABEL = gql`
	query countProgramVersionsForProgramWithSameLabel($where: [WhereExpressionGraph]) {
		countProgramVersionEntitys(where: $where) {
			number
		}
	}
`;

export const GET_PROGRAM_VERSION_ENTITY_WITH_STRUCTURES = gql`
	query programVersionEntity($args: [WhereExpressionGraph]) {
		programVersionEntity(where: $args) {
			id
			versionLabel
			status
			commencingYear
			versionDisplayName
			duration
			description
			duplicateDescription
			requirementFulfilmentDescription
			programRuleDescription
			studyPlanDescription
			programId
			structureOptionsDescription
			campuss {
				id
				campusId
				assignedProgramVersionId
				campus {
					id
					name
					institutionId
				}
			}
			program {
				id
				title
				code
				formalAbbreviation
			}
			duplicatedFromId
			duplicatedFrom {
				id
				versionLabel
			}
			studyPeriodConfigs {
				id
				minimum
				maximum
				programVersionId
				studyPeriod {
					id
					name
					order
				}
			}
			termConfigs(orderBy: [{path: "term.studyPeriod.order"}, {path: "term.order"}]) {
				id
				minimum
				maximum
				increment
				defaultLoad
				programVersionId
				term {
					id
					name
					order
					studyPeriodId
				}
			}
			programStructures {
				id
				name
				description
				programVersionId
				components {
					id
					created
					modified
					name
					displayName
					description
					requirementType
					displayOnSidebar
					order
					condition
					compulsory
					category
					condition
					order
					programStructureId
					rootProgramStructureId
					parentRequirementId
					showDiscontinuedCourses
					unitConditions {
						id
						ruleType
						units
						requirementId
					}
					${getRequirementQueryWithLevels(5)}
					courseOfferings {
						id
						studyPeriod
						yearLevel
						requirement
						prerequisite
						enabled
						note
						programRequirementId
						campusId
						campus {
							id
							name
						}
						termId
						term {
							id
							name
							order
							studyPeriodId
							studyPeriod {
								id
								name
							}
						}
						course {
							id
							code
							name
							level
							requisiteLogicGroups {
								id
								isTopLevelGroup
								description
							}
						}
					}
				}
			}
			rootProgramRules {
				id
				created
				modified
				name
				displayName
				description
				displayOnSidebar
				condition
				compulsory
				category
				parentProgramRuleId
				programVersionId
				rootProgramVersionId
				unitConditions {
					id
					ruleType
					units
					programRuleId
				}
				courses {
					id
					course {
						id
						code
						name
						level
						requisiteLogicGroups {
							id
							isTopLevelGroup
							description
						}
					}
				}
				${getProgramRulesRecursiveQuery(4)}
			}
		}
	}
`;

export const getProgramVersions = async (programId: string | undefined | null) => {
	if (!programId) {
		return;
	}

	const { data } = await store.apolloClient
		.query<{ programVersionEntitys: IProgramVersionEntityAttributes[] }>({
			query: new ProgramVersionEntity().fetchAllQuery,
			variables: {
				args: [
					{
						path: 'programId',
						comparison: 'equal',
						value: [programId],
					},
				],
				orderBy: [
					{
						path: 'modified',
						descending: true,
					},
				],
			},
			fetchPolicy: 'network-only',
		});

	return data.programVersionEntitys
		.map(versionAttributes => new ProgramVersionEntity(versionAttributes));
};

export const getProgramVersionWithStructures = async (programVersionId: string | undefined | null) => {
	if (!programVersionId) {
		return;
	}

	const { data } = await store.apolloClient
		.query<{ programVersionEntity: IProgramVersionEntityAttributes}>(
			{
				query: GET_PROGRAM_VERSION_ENTITY_WITH_STRUCTURES,
				fetchPolicy: 'network-only',
				variables: {
					args: [
						{
							path: 'id',
							comparison: 'equal',
							value: programVersionId,
						},
					],
				},
			},
		);

	return data;
};

export const countProgramVersionsWithSameLabel = async (
	programId: string | undefined | null,
	versionLabel: string | undefined | null,
	programVersionId?: string,
) => {
	if (!programId || !versionLabel) {
		return;
	}

	const { data } = await store.apolloClient
		.query<{ countProgramVersionEntitys: { number: number }}>(
			{
				query: COUNT_PROGRAM_VERSIONS_WITH_VERSION_LABEL,
				fetchPolicy: 'network-only',
				variables: {
					where: [
						{
							path: 'versionLabel',
							comparison: 'equal',
							value: versionLabel,
						},
						{
							path: 'programId',
							comparison: 'equal',
							value: programId,
						},
						...programVersionId !== undefined
							? (
								[{
									path: 'id',
									comparison: 'equal',
									negate: true,
									value: programVersionId,
								}]
							) : [],
					],
				},
			},
		);

	return data;
};

export const getProgramVersionNamesWithCampus = async (
	abortSignals: AbortSignals,
	conditions: Conditions,
) => {
	return store.apolloClient
		.query<{ programVersionEntitys: IProgramVersionEntityAttributes[]}>(
			{
				query: GET_PROGRAM_VERSION_NAMES_WITH_CAMPUS,
				fetchPolicy: 'network-only',
				variables: {
					conditions,
				},
				context: {
					fetchOptions: {
						signal: abortSignals.abortSignal,
					},
				},
			},
		);
};
